import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useMemo, useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
export { HiddenTitle } from '../dialog-common/dialog-common.js';
import { DEFAULT_DIALOG_ON_CLOSE_OPTS, DialogContext } from '../dialog-common/dialog-common.context.js';
export { useClose } from '../dialog-common/dialog-common.context.js';
import '../dialog-common/dialog-common.pages.js';
import '../dialog-common/dialog-common.sidebar.js';
import { Root as Root$1 } from '../dialog-primitive/dialog-primitive.js';
export { Label } from '../dialog-primitive/dialog-primitive.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { useTabLoop } from '../hooks/use-tab-loop.js';
import { asElement } from '../utils/dom.js';
import { addListener, PREVENT_EVENT_CAPTURE } from '../utils/event.js';
import { noop, callEach } from '../utils/fn.js';
import { focusElement } from '../utils/focus.js';
import { mergeProps } from '../utils/merge-props.js';
import { PersistentPopupOutletProvider } from '../utils/persistent-popup-outlet.js';
import { PopoverOutlet } from '../utils/popover-outlet.js';
import { MODAL_QUERY } from '../utils/query.js';
import { disablePageScroll } from '../utils/scroll.js';
import { resolveStyleValue } from '../utils/style-value.js';
import { root } from './modal-primitive.css.js';

/**
 * Root component for the unstyled "headless" component for a modal (used when there's a "fork in
 * the road" and the user needs to make a choice immediately). Use `Modal` for modals in the UI3
 * design system, and use `ModalPrimitive` when you need custom styling
 *
 * @see https://fpl.figma.design/components/primitives/modal/
 */
const Root = forwardRef((propsIn, refIn) => {
    const { styles: s = {}, children, manager, open: openProp, onClose: onCloseProp, preventUserClose: preventUserCloseIn = false, ...props } = propsIn;
    const ref = useSharedRef(refIn);
    // Determine which mode we're in. Precedence (highest to lowest):
    //   1. `manager` (legacy or new) — `open`/`onClose` are ignored if passed alongside.
    //   2. `open` + `onClose` — controlled mode.
    //   3. `onClose` only — mount/unmount mode (modal is visible while mounted).
    //   4. Nothing — falls back to a noop onClose so the modal still renders.
    // Conflicts are reported via console.error so a wrapper passing through extra props can't
    // crash the app.
    const legacyManager = manager && !isNewStyleManager(manager) ? manager : null;
    const newManager = manager && isNewStyleManager(manager) ? manager : null;
    if (manager && onCloseProp) {
        console.error(`ModalPrimitive.Root: \`onClose\` is ignored when a ${newManager ? 'ModalManager' : 'legacy manager'} is passed. Pass \`onClose\` to \`${newManager ? 'useModalManager' : 'useModal'}\` instead.`);
    }
    if (manager && openProp != null) {
        console.error(`ModalPrimitive.Root: \`open\` is ignored when a ${newManager ? 'ModalManager' : 'legacy manager'} is passed. ${newManager
            ? 'Use `defaultOpen` on `useModalManager` instead.'
            : 'Pass `open` to `useModal` instead.'}`);
    }
    if (!manager && !onCloseProp) {
        console.error('ModalPrimitive.Root: `onClose` is required when not using a manager. Falling back to a noop.');
    }
    const isOpen = legacyManager
        ? legacyManager.isOpen
        : newManager
            ? newManager.isOpen
            : (openProp ?? true);
    const preventUserClose = legacyManager
        ? preventUserCloseIn || legacyManager.preventUserClose
        : preventUserCloseIn;
    // For new manager and direct props, we need to create handlers. For legacy, these are
    // pre-computed in the manager's private bag — we pass a noop to the hook and override below.
    // When neither manager nor onClose was provided, we fall back to noop after warning above.
    const onCloseForHandler = legacyManager
        ? noop
        : newManager
            ? newManager.__onClose
            : (onCloseProp ?? noop);
    const rootStyles = resolveStyleValue(s.root);
    const backdropStyles = resolveStyleValue(s.backdrop);
    const contentsStyles = resolveStyleValue(s.contents);
    const recordedOnClose = useInteractionHandler(onCloseForHandler, { eventName: 'close', recordingKey: props.recordingKey }, [onCloseForHandler]);
    const close = legacyManager ? legacyManager.private.close : recordedOnClose;
    function handleClick(e) {
        if (!preventUserClose && e.target.matches('[data-backdrop]')) {
            recordedOnClose({ source: 'outside' });
        }
    }
    function handleKeyDown(e) {
        // Keyboard events should never escape the modal
        e.stopPropagation();
        if (!preventUserClose && e.code === 'Escape' && !e.repeat) {
            e.preventDefault();
            recordedOnClose({ source: 'escape' });
        }
    }
    const onClick = legacyManager ? legacyManager.private.onClick : handleClick;
    const onKeyDown = legacyManager ? legacyManager.private.onKeyDown : handleKeyDown;
    const hidden = legacyManager ? legacyManager.private.hidden : !isOpen;
    const tabLoopProps = useTabLoop();
    const contextValue = useMemo(() => ({ close: (opts = DEFAULT_DIALOG_ON_CLOSE_OPTS) => close(opts) }), [close]);
    useEffect(() => {
        if (isOpen) {
            return callEach(disablePageScroll(), addListener(document.body, 'focus', (e) => {
                const target = asElement(e.target);
                const thisModal = ref.current;
                if (!target || !thisModal)
                    return;
                const modal = target.closest(MODAL_QUERY);
                // Focus is still within this modal, nothing to do
                if (modal === thisModal)
                    return;
                // Need to see if we are the top modal. Assume that the topmost modal
                // is the last one in the dom.
                const allVisibleModals = Array.from(document.querySelectorAll(`${MODAL_QUERY}:not([hidden])`));
                // If focus shifted outside of this modal, and it is the top modal, bring focus back.
                const isTop = allVisibleModals.at(-1) === thisModal;
                if (isTop)
                    focusElement(thisModal);
            }, true));
        }
    }, [isOpen, ref]);
    return (jsx(DialogContext.Provider, { value: contextValue, children: jsxs(Root$1, { ...mergeProps(props, { onClick, onKeyDown, hidden }, tabLoopProps), ...PREVENT_EVENT_CAPTURE, ref: ref, "aria-modal": true, open: isOpen, className: clsx(root, rootStyles.className), style: rootStyles.style, children: [jsx("div", { "data-backdrop": true, ...backdropStyles }), jsx(PersistentPopupOutletProvider, { children: jsx(PopoverOutlet, { children: jsx("section", { ...contentsStyles, children: children }) }) })] }) }));
});
Root.displayName = 'ModalPrimitive.Root';
/**
 * Use this hook to set up a controlled modal. With this hook, the open/close state of the modal
 * is completely controlled by the value of the `open` prop. You must also provide an `onClose` prop
 * that updates the `open` state when called.
 *
 * @deprecated Prefer passing `open` and `onClose` directly to `Modal.Root` instead. The manager
 * pattern will be removed in a future version.
 */
function useModal({ open: isOpen, onClose: onCloseIn, preventUserClose = false, recordingKey, }) {
    const recordedOnClose = useInteractionHandler(onCloseIn, { eventName: 'close', recordingKey }, [
        onCloseIn,
    ]);
    function onClick(e) {
        if (!preventUserClose && e.target.matches('[data-backdrop]')) {
            recordedOnClose({ source: 'outside' });
        }
    }
    function onKeyDown(e) {
        // keyboard events should never escape the modal
        e.stopPropagation();
        if (!preventUserClose && e.code === 'Escape' && !e.repeat) {
            e.preventDefault();
            recordedOnClose({ source: 'escape' });
        }
    }
    return {
        isOpen,
        preventUserClose,
        private: { close: recordedOnClose, onClick, onKeyDown, hidden: !isOpen },
    };
}
/**
 * Use this hook to set up an uncontrolled modal. With this hook, the open/close state is managed
 * by the browser. `defaultOpen` can be set to default the modal to an open state. The `onClose`
 * callback is called after the modal has already been closed.
 *
 * The `manager` object returned by this hook has `open` and `close` methods which can be used to
 * imperatively open or close the modal.
 *
 * @note Uncontrolled modals cannot use React's `autoFocus` prop to open with a specific child
 * element focused since that happens on mount, and uncontrolled modals are always mounted. Use
 * the `useAutofocus` hook instead.
 *
 * @deprecated Use `useModalManager` instead for imperative modal control.
 */
function useUncontrolledModal({ defaultOpen = false, onClose: onCloseIn, onCancel, } = {}) {
    // We don't call useInteractionHandler here because recording doesn't make much sense for
    // uncontrolled components. The browser closes the modal and then fires the event, but if we
    // just fired the event then the modal would not actually be closed.
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const close = useCallback(() => setIsOpen(false), []);
    const open = useCallback(() => setIsOpen(true), []);
    const onClose = useCallback(() => {
        if (dispatchCancelEvent(onCancel))
            return;
        close();
        onCloseIn?.();
    }, [close, onCloseIn, onCancel]);
    const manager = useModal({ open: isOpen, onClose });
    return { ...manager, open, close };
}
/**
 * Hook for imperative control of a modal. The modal's open/close state is managed internally.
 * Use the returned `open`, `close`, and `toggle` methods to control it.
 *
 * Pass the returned `manager` object to `Modal.Root` or `ModalPrimitive.Root`.
 *
 * @example
 * ```tsx
 * const modal = Modal.useModalManager({ defaultOpen: false })
 *
 * return (
 *   <>
 *     <Button onClick={modal.open}>Open</Button>
 *     <Modal.Root manager={modal} width="md">
 *       <Modal.Contents>...</Modal.Contents>
 *     </Modal.Root>
 *   </>
 * )
 * ```
 */
function useModalManager({ defaultOpen = false, onClose: onCloseIn, } = {}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const close = useCallback(() => setIsOpen(false), []);
    const open = useCallback(() => setIsOpen(true), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
    const onClose = useCallback((opts) => {
        if (onCloseIn) {
            const event = new CustomEvent('close', {
                cancelable: true,
                detail: { source: opts.source },
            });
            onCloseIn(event);
            if (event.defaultPrevented)
                return;
        }
        close();
    }, [close, onCloseIn]);
    // We store onClose on the manager so that Root can access it when determining whether to use
    // the `onClose` from the manager or from props. This avoids the consumer having to pass onClose
    // to both the hook and the Root.
    return useMemo(() => ({
        isOpen,
        open,
        close,
        toggle,
        __brand: 'ModalManager',
        __onClose: onClose,
    }), [isOpen, open, close, toggle, onClose]);
}
function dispatchCancelEvent(fn) {
    if (!fn)
        return false;
    const e = new CustomEvent('cancel', { cancelable: true });
    fn(e);
    return e.defaultPrevented;
}
function isNewStyleManager(manager) {
    return (manager != null &&
        typeof manager === 'object' &&
        '__brand' in manager &&
        manager.__brand === 'ModalManager');
}

export { Root, useModal, useModalManager, useUncontrolledModal };
//# sourceMappingURL=modal-primitive.js.map
